/**
 * LiquidGlass primitives.
 *
 * Real depth and refraction — not just backdrop-blur.
 *
 * Layers (back to front):
 *   1. Optional SVG displacement filter applied to the backdrop (refraction)
 *   2. Backdrop blur + saturation boost (true frosted glass)
 *   3. Translucent surface tint (very subtle, mostly transparent)
 *   4. Inner highlight: top edge linear gradient (the wet rim)
 *   5. Inner shadow: bottom curve gradient (depth)
 *   6. Chromatic ring: 2px stacked borders with cyan/magenta micro-offset
 *   7. Optional noise grain (turbulence) at very low opacity
 *
 * Inspired by Apple visionOS, Stripe checkout, Linear pricing comparison.
 */
import * as React from "react";
import { cn } from "@/lib/utils";

/* ----------------------------------------------------------------- */
/* Global SVG filter defs — mount once at the page root              */
/* ----------------------------------------------------------------- */
export function LiquidGlassDefs() {
  return (
    <svg
      aria-hidden
      width="0"
      height="0"
      style={{ position: "absolute", pointerEvents: "none" }}
    >
      <defs>
        {/* Subtle film grain — used as a CSS filter url(#lg-grain) */}
        <filter id="lg-grain">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.9"
            numOctaves="2"
            stitchTiles="stitch"
          />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1
                    0 0 0 0 1
                    0 0 0 0 1
                    0 0 0 0.06 0"
          />
        </filter>

        {/* Refractive distortion for backdrop content */}
        <filter id="lg-refract">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.012 0.018"
            numOctaves="2"
            seed="3"
          />
          <feDisplacementMap in="SourceGraphic" scale="6" />
        </filter>
      </defs>
    </svg>
  );
}

/* ----------------------------------------------------------------- */
/* Page-level grain overlay — sits over everything at very low alpha */
/* ----------------------------------------------------------------- */
export function GrainOverlay({ opacity = 0.04 }: { opacity?: number }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[60] mix-blend-overlay"
      style={{
        opacity,
        backgroundImage:
          // SVG fractalNoise data-URI (smaller + crisper than PNG)
          `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.7 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>")`,
      }}
    />
  );
}

/* ----------------------------------------------------------------- */
/* LiquidGlass surface                                                */
/* ----------------------------------------------------------------- */
type LGProps = React.HTMLAttributes<HTMLDivElement> & {
  /** corner radius in px (default 18) */
  radius?: number;
  /** elevation: low|mid|high — controls shadow + chromatic ring intensity */
  elevation?: "low" | "mid" | "high";
  /** accent tint of the inner glow (CSS hsl-without-wrapper string from tokens) */
  tint?: string;
  /** disable the chromatic edge */
  noChroma?: boolean;
  children?: React.ReactNode;
};

export const LiquidGlass = React.forwardRef<HTMLDivElement, LGProps>(
  function LiquidGlass(
    {
      className,
      radius = 18,
      elevation = "mid",
      tint = "var(--rd-accent-500)",
      noChroma = false,
      style,
      children,
      ...rest
    },
    ref
  ) {
    const shadow =
      elevation === "high"
        ? "0 30px 80px -30px rgba(0,0,0,0.65), 0 8px 24px -10px rgba(0,0,0,0.45)"
        : elevation === "low"
        ? "0 8px 24px -10px rgba(0,0,0,0.35)"
        : "0 18px 50px -20px rgba(0,0,0,0.55), 0 4px 14px -6px rgba(0,0,0,0.35)";

    return (
      <div
        ref={ref}
        className={cn("relative isolate", className)}
        style={{
          borderRadius: radius,
          ...style,
        }}
        {...rest}
      >
        {/* Layer 1: frosted glass surface (the backdrop blur lives here) */}
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            borderRadius: radius,
            background:
              "linear-gradient(155deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.025) 35%, rgba(255,255,255,0.05) 60%, rgba(255,255,255,0.015) 100%)",
            backdropFilter: "blur(22px) saturate(160%)",
            WebkitBackdropFilter: "blur(22px) saturate(160%)",
            boxShadow: shadow,
          }}
        />

        {/* Layer 2: subtle accent tint (very faint, gives the panel its color identity) */}
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            borderRadius: radius,
            background: `radial-gradient(120% 80% at 0% 0%, hsl(${tint} / 0.10), transparent 55%), radial-gradient(120% 80% at 100% 100%, hsl(${tint} / 0.06), transparent 55%)`,
          }}
        />

        {/* Layer 3: inner highlight rim (the "wet" top edge) */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            borderRadius: radius,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 18%, transparent 28%)",
            mixBlendMode: "overlay",
          }}
        />

        {/* Layer 4: inner shadow at bottom (depth) */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            borderRadius: radius,
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.05)",
          }}
        />

        {/* Layer 5: chromatic edge ring — two stacked offset borders */}
        {!noChroma && (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                borderRadius: radius,
                boxShadow:
                  "0 0 0 1px rgba(120, 200, 255, 0.10), 0 0 0 1.5px rgba(255, 110, 230, 0.05)",
              }}
            />
          </>
        )}

        {/* Content */}
        <div className="relative">{children}</div>
      </div>
    );
  }
);
